// How loud a sound is and where it sits, as arithmetic.
//
// Pure and fully tested, because everything downstream of it is a browser: the
// engine drops sounds, the port talks to an `AudioContext`, and neither can say
// whether a piece three screens away should be audible. That question is this
// file, and it has one property worth stating out loud — **distance is measured
// in view-heights, not in squares**. The board grows for a whole run and the
// camera zooms out to hold it, so a fixed square radius would make the same
// move loud on an eight-by-eight and silent on a board four fronts in. A share
// of what is on screen is the same question at any zoom.

/** A gain in 0..1 and a pan in -1..1, which is what a voice needs to be placed. */
export interface Mix {
  gain: number;
  pan: number;
}

/**
 * Read a stored volume, falling back on anything that is not a fraction.
 *
 * The value comes off `localStorage`, which is a store a person is allowed to
 * edit and which holds whatever shape shipped the day they last played — so
 * `undefined`, a string and `NaN` all have to arrive as something a gain node
 * can be ramped to. Clamped rather than rejected above 1: a hand-edited 4 is a
 * legible request for "loud", and the answer to it is the loudest there is.
 *
 * The fallback is code-supplied and returned as given.
 */
export function clampVolume(v: unknown, fallback: number): number {
  if (typeof v !== "number" || !Number.isFinite(v)) return fallback;
  return Math.min(1, Math.max(0, v));
}

/**
 * What a slider at `v` means as a gain.
 *
 * Squared, because loudness is not linear in amplitude: a linear slider spends
 * its top half on differences nobody can hear and its bottom half going from
 * "quiet" to "off" in a few pixels. `v * v` puts the useful range under the
 * hand. Cheap enough to be exact and monotone, which is all a control needs.
 */
export const sliderGain = (v: number): number => v * v;

/**
 * Where the camera is looking, in the board's own world units.
 *
 * A structural copy of what `CameraRig.pose` reports rather than an import:
 * `src/client/ai/audio/` depends on nothing, so the renderer hands these four numbers
 * across rather than the audio layer reaching into the renderer for them.
 */
export interface ListenerPose {
  /** Board yaw in radians. The board turns; the listener does not. */
  yaw: number;
  focusX: number;
  focusZ: number;
  /** World units of board filling the screen vertically. The zoom. */
  view: number;
}

/**
 * Inside this many view-heights of the focus a sound is at full gain.
 *
 * Half, so the middle of the screen is simply audible — a falloff that starts
 * at the focus point makes every sound quieter than the one before it for no
 * reason a player could name.
 */
export const AUDIBLE_NEAR = 0.5;

/**
 * Past this many view-heights a sound is silent.
 *
 * One and a half, so the edge of the screen is still clearly audible and the
 * ground beyond it fades rather than cutting. A run's board runs well past the
 * viewport, and hearing every camp work at once is noise rather than presence.
 */
export const AUDIBLE_FAR = 1.5;

/**
 * How far a sound at the edge of the screen is pushed to one side.
 *
 * Half, deliberately subtle. A hard-panned board game is fatiguing over the
 * hour a run takes, and the pan is here to say *roughly where*, not to place a
 * sound in a room.
 */
export const PAN_WIDTH = 0.5;

/**
 * Below this many world units of view, the camera's height costs nothing.
 *
 * The distance falloff above is a share of the screen, so on its own zooming
 * *out* makes more of the board audible — reported from the playtest as no
 * falloff at all, because zooming out was exactly how it was tested. This is
 * the other axis: the listener rises with the camera. Sixteen is past the
 * classic board's deepest zoom, so an eight-by-eight — one room, every square
 * in earshot — never pays it.
 */
export const ALTITUDE_NEAR = 16;

/** By this view the board is a map, and a map is quiet. */
export const ALTITUDE_FAR = 60;

/**
 * The floor the altitude fade lands on, not silence.
 *
 * A watched run zoomed all the way out is still a game being played, and a
 * board that goes fully dead reads as the sound breaking rather than as
 * distance. Non-positional sounds (the board rising, mining's coalesced
 * pickup) never pay the altitude at all.
 */
export const ALTITUDE_FLOOR = 0.15;

/** Smooth 1 → 0 across `t` in 0..1, flat at both ends. */
function fade(t: number): number {
  const s = Math.min(1, Math.max(0, t));
  return 1 - s * s * (3 - 2 * s);
}

/**
 * Place a sound at board `(x, z)` against the camera.
 *
 * The offset from the focus is rotated by `-yaw` into view space, so what
 * decides the side is where the thing *is on screen* rather than which way is
 * north — the board snaps through four yaws and a sound that swapped ears when
 * somebody turned the view would read as a bug. At yaw 0 view-space X is board
 * X; at a quarter turn it is board Z.
 *
 * Distance needs no rotation (it is invariant), so gain is taken from the raw
 * offset and only the pan pays for the trigonometry.
 */
export function positionalMix(pose: ListenerPose, x: number, z: number): Mix {
  // A pose with no zoom cannot place anything, and a silent sound is a worse
  // answer than a plain one: play it as though it were at the focus.
  if (!Number.isFinite(pose.view) || pose.view <= 0) return { gain: 1, pan: 0 };

  const dx = x - pose.focusX;
  const dz = z - pose.focusZ;
  const r = Math.hypot(dx, dz) / pose.view;

  const near = r <= AUDIBLE_NEAR ? 1 : fade((r - AUDIBLE_NEAR) / (AUDIBLE_FAR - AUDIBLE_NEAR));
  // The camera's height, multiplied in: `fade` runs 1 → 0 across the zoom, so
  // this runs 1 → the floor. Two axes on purpose — panning away from the
  // action fades it at any zoom, and zooming out fades everything at once.
  const altitude =
    ALTITUDE_FLOOR +
    (1 - ALTITUDE_FLOOR) * fade((pose.view - ALTITUDE_NEAR) / (ALTITUDE_FAR - ALTITUDE_NEAR));
  const gain = near * altitude;

  const viewX = dx * Math.cos(pose.yaw) + dz * Math.sin(pose.yaw);
  const pan = Math.min(1, Math.max(-1, viewX / pose.view)) * PAN_WIDTH;

  return { gain, pan };
}
